import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import axios from "../../../../utils/axios";
import { useDialog } from "../../../../contexts/DialogContext";
import { showErrorSnackbar, showSuccessSnackbar } from "../../../../redux/slices/snackbar";

const CONFIRM_WORD = "delete";
// Backing up and deleting a large franchise can take a while
const DELETE_TIMEOUT_MS = 10 * 60 * 1000;

const ItemTable = ({ items, total }) => (
  <Box sx={{ maxHeight: 280, overflow: "auto" }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>Data</TableCell>
          <TableCell align="right">Records</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.table + item.label}>
            <TableCell>{item.label}</TableCell>
            <TableCell align="right">{item.count}</TableCell>
          </TableRow>
        ))}
        {total !== undefined && (
          <TableRow>
            <TableCell>
              <b>Total</b>
            </TableCell>
            <TableCell align="right">
              <b>{total}</b>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </Box>
);

function DeleteTenantModal({ tenantId, onDeleted }) {
  const dispatch = useDispatch();
  const { unSetDialog } = useDialog();

  const [check, setCheck] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [checkError, setCheckError] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setIsChecking(true);
    axios
      .get("/cpanel/tenant/delete-check/" + tenantId, { timeout: DELETE_TIMEOUT_MS })
      .then((response) => active && setCheck(response.data))
      .catch((err) => active && setCheckError(err.message || "Failed to check franchise"))
      .finally(() => active && setIsChecking(false));
    return () => {
      active = false;
    };
  }, [tenantId]);

  const isConfirmed =
    confirmText.trim().toLowerCase() === CONFIRM_WORD && (!check?.hasBusinessData || acknowledged);

  const onDelete = async () => {
    try {
      setIsDeleting(true);
      await axios.post(
        "/cpanel/tenant/delete/" + tenantId,
        { confirmText: confirmText.trim(), acknowledgeBusinessData: acknowledged },
        { timeout: DELETE_TIMEOUT_MS },
      );
      dispatch(showSuccessSnackbar("Franchise deleted. Backup saved in Delete History."));
      unSetDialog("delete_tenant");
      if (onDeleted) onDeleted();
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to delete franchise"));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isChecking) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={6}>
        <CircularProgress />
        <Typography variant="body2">Checking mapped data...</Typography>
      </Box>
    );
  }

  if (checkError) {
    return <Alert severity="error">{checkError}</Alert>;
  }

  const walletBalance = Number(check.walletBalance || 0);

  return (
    <React.Fragment>
      <Typography variant="subtitle1" gutterBottom>
        Franchise: <b>{check.tenantName}</b>
        {check.businessType ? ` (${check.businessType})` : ""}
      </Typography>

      {!check.deletable ? (
        <React.Fragment>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This franchise cannot be deleted. Resolve the following first.
          </Alert>
          <ItemTable items={check.blockers} />
          <DialogActions>
            <Button variant="text" onClick={() => unSetDialog("delete_tenant")} color="primary">
              Close
            </Button>
          </DialogActions>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Alert severity="error" sx={{ mb: 2 }}>
            This permanently deletes the franchise and all data below. Every record is backed up
            first and can be viewed or downloaded from Delete History.
          </Alert>
          {check.hasBusinessData && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Users, renewals / invoices and payment records of this franchise will be deleted.
              Online users will stop working.
            </Alert>
          )}
          {walletBalance !== 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Wallet balance of <b>₹{walletBalance.toFixed(2)}</b> will be lost.
            </Alert>
          )}
          <ItemTable items={check.willDelete} total={check.totalRows} />

          {check.hasBusinessData && (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
              }
              label="I understand users, billing and payment data will be deleted"
            />
          )}
          <Typography variant="body2" mt={2} mb={2}>
            Type <b>{CONFIRM_WORD}</b> to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={CONFIRM_WORD}
            value={confirmText}
            disabled={isDeleting}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isConfirmed && !isDeleting) onDelete();
            }}
          />
          <DialogActions>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              endIcon={isDeleting ? <CircularProgress size={20} /> : null}
              disabled={!isConfirmed || isDeleting}
              onClick={onDelete}
            >
              {isDeleting ? "Backing up & deleting..." : "Delete"}
            </Button>
            <Button
              variant="text"
              disabled={isDeleting}
              onClick={() => unSetDialog("delete_tenant")}
              color="primary"
            >
              Cancel
            </Button>
          </DialogActions>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default DeleteTenantModal;
